import { addSeconds, differenceInSeconds, isMatch, isValid, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

/**
 * Maximum elapsed time for questionnaire timer controls (labor shoulder dystocia, PPH, etc.).
 * Equivalent to 23 hours, 59 minutes, and 59 seconds (86,399 seconds; just under 24 hours).
 */
export const MAX_ELAPSED_TIMER_DURATION_SECONDS = 23 * 3600 + 59 * 60 + 59;

const UTC_EPOCH = new Date(0);

/**
 * Clamps elapsed timer seconds to the supported HH:mm:ss range (00:00:00–23:59:59).
 */
export function clampElapsedTimerDurationSeconds(totalSeconds: number): number {
  return Math.min(Math.max(0, Math.floor(totalSeconds)), MAX_ELAPSED_TIMER_DURATION_SECONDS);
}

function utcDateFromDurationSeconds(totalSeconds: number): Date {
  return addSeconds(UTC_EPOCH, totalSeconds);
}

/**
 * Parses an elapsed duration `HH:mm:ss` (00:00:00–23:59:59) to seconds, or null if invalid.
 */
export function parseHoursMinutesSecondsToSeconds(value: unknown): number | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed || !isMatch(trimmed, "HH:mm:ss")) {
    return null;
  }

  const parsed = parseISO(`1970-01-01T${trimmed}Z`);
  if (!isValid(parsed)) {
    return null;
  }

  if (formatInTimeZone(parsed, "UTC", "HH:mm:ss") !== trimmed) {
    return null;
  }

  const totalSeconds = differenceInSeconds(parsed, UTC_EPOCH);
  if (totalSeconds < 0 || totalSeconds > MAX_ELAPSED_TIMER_DURATION_SECONDS) {
    return null;
  }

  return totalSeconds;
}

/**
 * Formats elapsed seconds as `HH:mm:ss` (UTC), clamped to {@link MAX_ELAPSED_TIMER_DURATION_SECONDS}.
 */
export function formatSecondsAsHoursMinutesSeconds(totalSeconds: number): string {
  const clamped = clampElapsedTimerDurationSeconds(totalSeconds);
  return formatInTimeZone(utcDateFromDurationSeconds(clamped), "UTC", "HH:mm:ss");
}
